/**
 * Everything the Settings page does that is not rendering.
 *
 * Reporting "Anniversary dates saved." unconditionally, discarding the
 * errors it just collected, would make a typo look like it saved. The API
 * returns `saved` alongside `errors`, and `saveOutcome` below is what turns
 * that into the thing a person reads.
 *
 * Validation itself stays on the server, in `settings_logic`. A second
 * MM-DD parser in TypeScript would be a second definition of a valid date, and
 * the two would drift.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";

export interface AnniversaryCard {
  id: number;
  label: string;
  anniversary_date: string;
}

export interface AnniversaryRow {
  display_name: string;
  anniversary_date: string;
}

export interface SettingsDto {
  needing_anniversary: AnniversaryCard[];
  all_dates: AnniversaryRow[];
  warn_days_before: number;
  card_count: number;
}

export interface SaveResult {
  saved: boolean;
  updated: number[];
  errors: string[];
}

export async function fetchSettings(): Promise<SettingsDto> {
  const res = await authedFetch("/api/settings");
  return (await orThrow(res, "Could not load settings")) as SettingsDto;
}

export async function saveAnniversaries(
  values: Record<number, string>,
): Promise<SaveResult> {
  const res = await authedFetch("/api/settings/anniversaries", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ values }),
  });
  return (await orThrow(res, "Could not save anniversary dates")) as SaveResult;
}

export async function saveWarnDays(days: number): Promise<number> {
  const res = await authedFetch("/api/settings/warn-days", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ warn_days_before: days }),
  });
  const body = (await orThrow(res, "Could not save preferences")) as {
    warn_days_before: number;
  };
  return body.warn_days_before;
}

export type Outcome =
  | { kind: "success"; message: string }
  | { kind: "partial"; message: string; errors: string[] }
  | { kind: "failure"; errors: string[] };

/**
 * What to tell the user after a save.
 *
 * **This function is the fix.** The three cases are distinct on purpose:
 *
 *   success  everything applied
 *   partial  some entries applied and some were rejected -- both facts stated,
 *            because saying only "saved" is the original bug and saying only
 *            "failed" would hide writes that really happened
 *   failure  nothing applied
 *
 * There is deliberately no path that returns a success message while `errors`
 * is non-empty.
 */
export function saveOutcome(result: SaveResult): Outcome {
  if (result.saved && result.errors.length === 0) {
    return { kind: "success", message: "Anniversary dates saved." };
  }
  if (result.updated.length > 0) {
    return {
      kind: "partial",
      message: `Saved ${result.updated.length} of ${
        result.updated.length + result.errors.length
      }. The rest were not changed:`,
      errors: result.errors,
    };
  }
  return { kind: "failure", errors: result.errors };
}
